import { useEffect, useRef, useState } from 'react'
import { connectLiveSocket, type LiveServerFrame } from '../lib/live-socket'

/**
 * useResidentLive keeps the resident's conversation page fresh over the same
 * live socket the rest of the app uses (spec 2026-09-08-03).
 *
 * Why a dedicated hook rather than LiveEventsProvider: that provider is built
 * around an org-scoped, authenticated identity (a bearer token, a refcounted
 * registry of scopes many components share, TanStack Query invalidation per
 * scope). The resident page has none of that — it renders under BareShell with
 * no `:org` route param, holds no session, and cares about exactly one thing.
 * What it does NOT do is fork the transport: `connectLiveSocket` is the one
 * socket implementation, here in its reply-token mode.
 *
 * The server pushes the WHOLE view (the same projection
 * `GET /c/{org}/{token}/state` returns), so there is nothing to re-fetch and
 * nothing to invalidate: `onView` is handed the new page data directly.
 */
export function useResidentLive(
    orgCode: string,
    replyToken: string,
    onView: (view: unknown) => void,
): { isLive: boolean } {
    const [isLive, setIsLive] = useState(false)
    // onView is called from a socket callback that outlives any single render,
    // so it is read through a ref — otherwise a caller passing an inline
    // closure (the normal case) would tear the connection down and reconnect
    // on every render.
    const onViewRef = useRef(onView)
    useEffect(() => {
        onViewRef.current = onView
    }, [onView])

    useEffect(() => {
        if (!orgCode || !replyToken) return

        const handle = connectLiveSocket(
            orgCode,
            () => null, // a resident has no session token, by design
            {
                onOpen: () => setIsLive(true),
                onFrame: (frame: LiveServerFrame) => {
                    if (frame.type === 'view') onViewRef.current(frame.view)
                },
                onClose: () => setIsLive(false),
            },
            { replyToken },
        )

        return () => handle.close()
    }, [orgCode, replyToken])

    return { isLive }
}
